import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ModelOption } from 'services/scan-config';
import { PopoverController } from 'providers/popover-controller';
import { CsSelectPopover, CsSelectPopoverOption } from 'components/cs-select-popover/cs-select-popover';
import { untranslatedString } from 'models/translated-string';
import { BackdropMode } from 'components/popover-host/popover-host';
import { hasClientSmallScreen } from 'environments/environment-helpers';

/**
 * The 'any'-mode model picker shared by all AI scanners (#3783). It is
 * the wand button in the shutter bar's left slot: tapping it opens a popup of
 * the configured models and emits the chosen key. Extracted from the label
 * scanner's bespoke picker so every scanner exposes the same options.
 *
 * It only appears when the provider config value is literally 'any' — the
 * parent page gates it with `@if (scanMode === 'any')`. The 'any' value is
 * itself the gate (there is no separate debug flag), so normal users on a
 * model-list config never see it. See design-docs/scanner-model-config.md.
 */
@Component({
  selector: 'cs-scanner-model-picker',
  templateUrl: 'cs-scanner-model-picker.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CsScannerModelPickerComponent {
  /** The pickable models, built by parseScanConfig for 'any' mode. */
  @Input() modelOptions: ModelOption[] = [];
  /** The currently selected model key (highlighted in the popup). */
  @Input() selectedModel = '';
  /** Emits the chosen model key when the user picks a different one. */
  @Output() modelSelected = new EventEmitter<string>();

  constructor(private popoverCtrl: PopoverController) {}

  async openModelSelector(event: Event) {
    const isSmallScreen = hasClientSmallScreen();
    const targetEl = (event.currentTarget as HTMLElement);
    const options: CsSelectPopoverOption[] = this.modelOptions.map(opt => ({
      // AI model product names ("Gemini 2.5 Flash", "GPT-5 mini") are brand names from the
      // SCAN_MODELS registry. Product names are not translated in any language.
      name: untranslatedString(opt.label),
      value: opt.key,
    }));

    const result = await this.popoverCtrl.present(CsSelectPopover, {
      title: untranslatedString(''),
      options,
      value: this.selectedModel,
      bottomSheet: isSmallScreen,
    }, {
      targetFromElement: targetEl,
      targetToElement: targetEl,
      animate: true,
      bottomSheet: isSmallScreen,
      verticalAlign: 'top',
      backdropMode: isSmallScreen ? BackdropMode.Visible : BackdropMode.Invisible,
    });

    if (result?.value !== undefined && result.value !== this.selectedModel) {
      this.modelSelected.emit(result.value);
    }
  }
}
